/**
 * sidebar-progress.js
 *
 * Sidebar nav-indicator widths react to scroll position with a per-frame
 * spring lerp so transitions feel like water flowing between links.
 *
 * Algorithm
 *  - Pick the active section by largest viewport-overlap (stable through
 *    a tall section, with a discrete handoff at the boundary).
 *  - Each sidebar link has a *target* indicator width derived from its
 *    |sidebarIdx - activeIdx| distance:
 *        d=0  → 24px (active, fully open)
 *        d=1  → 15px
 *        d=2  →  5px
 *        d=3+ →  2px (dot)
 *    Linear interpolation handles sub-integer distances when the active
 *    is in motion.
 *  - A requestAnimationFrame loop interpolates each link's *current*
 *    visual width toward its target with a Hooke-style lerp. The lerp
 *    coefficient decays with distance from the active link so closer
 *    links snap fast and far links lag a beat — the change propagates
 *    outward from the active link like a ripple across the sidebar.
 *  - We keep the rAF loop alive only while at least one link is still
 *    converging, then idle until the next scroll event.
 *
 * Respects prefers-reduced-motion (kills the lerp; widths snap instantly).
 */

const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const sections = Array.from(document.querySelectorAll('section[data-nav]'));
const sidebarLinks = Array.from(document.querySelectorAll('a[data-nav-link]'));

// nav-key (e.g. "direction") → sidebar position
const sidebarIdxByNav = new Map();
sidebarLinks.forEach((link, idx) => {
  const id = link.getAttribute('href')?.slice(1);
  if (id) sidebarIdxByNav.set(id, idx);
});

// Each page section paired with its sidebar position
const pageSections = sections
  .map((section) => {
    const navKey = section.getAttribute('data-nav');
    const sidebarIdx = sidebarIdxByNav.get(navKey);
    return sidebarIdx !== undefined ? { section, navKey, sidebarIdx } : null;
  })
  .filter(Boolean);

// Width by integer distance from active. Linear interp between stops.
// Far/inactive links rest at 4px so they read as a 4×4 circle (indicator
// height is 4px); active grows to 24px (pill).
const WIDTH_AT_DIST = [18, 12, 5, 3];

function widthForDistance(d) {
  if (d <= 0) return WIDTH_AT_DIST[0];
  if (d >= 3) return WIDTH_AT_DIST[3];
  const lo = Math.floor(d);
  const hi = lo + 1;
  const t = d - lo;
  return WIDTH_AT_DIST[lo] + (WIDTH_AT_DIST[hi] - WIDTH_AT_DIST[lo]) * t;
}

function computeFractionalActiveIndex() {
  // Return a *fractional* sidebar index that interpolates between
  // adjacent sections as the viewport-centre crosses their midpoints.
  // This is the key to fluid section-to-section transitions — target
  // indicator widths shift continuously instead of jumping discretely
  // at the section boundary.
  if (pageSections.length === 0) return 0;
  const vh = window.innerHeight;
  const viewportCenter = vh / 2;

  // Snapshot each section's *centre* in viewport coordinates, sorted
  // top → bottom (page order — not necessarily sidebar order).
  const centres = pageSections
    .map((entry) => {
      const r = entry.section.getBoundingClientRect();
      return { center: (r.top + r.bottom) / 2, sidebarIdx: entry.sidebarIdx };
    })
    .sort((a, b) => a.center - b.center);

  // Edge cases: before the first centre or past the last centre.
  if (viewportCenter <= centres[0].center) return centres[0].sidebarIdx;
  const last = centres[centres.length - 1];
  if (viewportCenter >= last.center) return last.sidebarIdx;

  // Find the two centres that bracket the viewport centre and lerp
  // between their sidebar indices.
  for (let i = 0; i < centres.length - 1; i++) {
    const a = centres[i];
    const b = centres[i + 1];
    if (a.center <= viewportCenter && viewportCenter <= b.center) {
      const t = (viewportCenter - a.center) / (b.center - a.center);
      return a.sidebarIdx + (b.sidebarIdx - a.sidebarIdx) * t;
    }
  }
  return last.sidebarIdx;
}

// ──────────── Spring/ripple state ────────────
// Each link has a "current" visual width (what's painted) and a "target"
// width (what it should be at). Per frame, current eases toward target.
const linkState = sidebarLinks.map(() => ({ current: 4, target: 4 }));
let activeFractional = 0; // fractional sidebar index — see compute fn
let activeIntIdx = 0; // discrete rounded version for aria-current
let rafHandle = null;

// Lerp factor by distance from active. Closer-to-active links converge
// faster than far links — that's what makes the change *propagate* outward
// rather than every link snapping in lockstep.
function lerpFactor(distance) {
  const base = 0.32;
  const tail = 0.1;
  const k = Math.min(distance / 4, 1);
  return base + (tail - base) * k;
}

function applyCurrent() {
  sidebarLinks.forEach((link, idx) => {
    link.style.setProperty('--indicator-width', `${linkState[idx].current.toFixed(2)}px`);
  });
}

function tick() {
  let stillMoving = false;
  sidebarLinks.forEach((_, idx) => {
    const s = linkState[idx];
    // Distance is from the *fractional* active position — when the user
    // is mid-transition between two sections, BOTH neighbouring links
    // sit at sub-integer distance ≈0.5 and share the wide width. That's
    // what makes the handoff feel like fluid — no hard switch moment.
    const dist = Math.abs(idx - activeFractional);
    const lambda = lerpFactor(dist);
    const delta = s.target - s.current;
    if (Math.abs(delta) < 0.05) {
      s.current = s.target;
    } else {
      s.current += delta * lambda;
      stillMoving = true;
    }
  });
  applyCurrent();
  if (stillMoving) {
    rafHandle = requestAnimationFrame(tick);
  } else {
    rafHandle = null;
  }
}

// `let` (not a function declaration) because reduced-motion mode reassigns
// this below to snap instantly instead of lerping.
let scheduleTick = () => {
  if (rafHandle == null) rafHandle = requestAnimationFrame(tick);
};

function recomputeTargets() {
  if (pageSections.length === 0) return;
  activeFractional = computeFractionalActiveIndex();
  const newIntActive = Math.round(activeFractional);
  if (newIntActive !== activeIntIdx) {
    sidebarLinks.forEach((link, idx) => {
      if (idx === newIntActive) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    activeIntIdx = newIntActive;
  } else {
    sidebarLinks[activeIntIdx]?.setAttribute('aria-current', 'true');
  }
  // Each link's target indicator width is set from its *fractional*
  // distance to the active position. widthForDistance already linearly
  // interpolates between width stops, so sub-integer distances render
  // smoothly without snapping.
  sidebarLinks.forEach((_, idx) => {
    const d = Math.abs(idx - activeFractional);
    linkState[idx].target = widthForDistance(d);
  });
  scheduleTick();
}

// rAF-batched scroll handler
let pending = false;
function onScroll() {
  if (pending) return;
  pending = true;
  requestAnimationFrame(() => {
    pending = false;
    recomputeTargets();
  });
}

window.addEventListener('scroll', onScroll, { passive: true });
window.addEventListener('resize', onScroll);

// In reduced-motion mode, freeze every link at its target — no lerp.
if (prefersReducedMotion) {
  // Override tick to apply target instantly.
  function snapTick() {
    sidebarLinks.forEach((_, idx) => {
      linkState[idx].current = linkState[idx].target;
    });
    applyCurrent();
    rafHandle = null;
  }
  scheduleTick = () => {
    if (rafHandle == null) rafHandle = requestAnimationFrame(snapTick);
  };
  // Kill the CSS transition too (in case base.css doesn't catch it).
  document.documentElement.style.setProperty('--nav-indicator-transition', 'none');
}

// Initial paint — target widths from current scroll, then ripple to them.
// Start "current" at 2px (rest dot) so the first paint *animates* in,
// showing the user the indicator system is alive.
sidebarLinks.forEach((_, idx) => {
  linkState[idx].current = 2;
});
recomputeTargets();
